import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  Phone, Users as UsersIcon, CheckSquare, Clock, Mail, Plus, Trash2, Pencil, Check, X
} from 'lucide-react';
import api, { organizationsAPI } from '../services/api';
import CustomFieldsInput from './CustomFieldsInput';
import CustomFieldsDisplay from './CustomFieldsDisplay';
import TaskEditModal from './TaskEditModal';
import { useAuth } from '../contexts/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const TYPE_META = {
  call:     { label: 'Call',     Icon: Phone,       color: 'text-green-700'  },
  meeting:  { label: 'Meeting',  Icon: UsersIcon,   color: 'text-blue-700'   },
  todo:     { label: 'To-do',    Icon: CheckSquare, color: 'text-gray-700'   },
  deadline: { label: 'Deadline', Icon: Clock,       color: 'text-red-700'    },
  email:    { label: 'Email',    Icon: Mail,        color: 'text-purple-700' }
};

const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };

// Meta-style urgency tint per due date: red (overdue) > red (today) >
// amber (this week) > emerald (this month) > neutral (later). Keeps the
// lead-page tasks visually in sync with the main Tasks page.
const dueAccent = (dueDate) => {
  if (!dueDate) return { row: 'border-gray-200 bg-white', text: 'text-gray-500' };
  const today = startOfDay(new Date()).getTime();
  const due = startOfDay(new Date(dueDate)).getTime();
  if (due < today) return { row: 'border-red-300 bg-red-100', text: 'text-red-900 font-semibold' };
  if (due === today) return { row: 'border-red-200 bg-red-50', text: 'text-red-700 font-medium' };
  if (due <= today + 6 * 24 * 3600 * 1000) return { row: 'border-amber-200 bg-amber-50', text: 'text-amber-700' };
  if (due <= today + 30 * 24 * 3600 * 1000) return { row: 'border-emerald-200 bg-emerald-50', text: 'text-emerald-700' };
  return { row: 'border-gray-200 bg-white', text: 'text-gray-500' };
};

const formatDue = (d) => {
  if (!d) return 'No due date';
  const dt = new Date(d);
  const today = startOfDay(new Date()).getTime();
  const diff = (startOfDay(dt).getTime() - today) / (24 * 3600 * 1000);
  if (diff === 0) return `Today, ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  if (diff === 1) return `Tomorrow, ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff < 7) return `In ${diff}d`;
  return dt.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const LeadTasksWidget = ({ leadId }) => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const canAssignAnyone = currentUser?.permissions?.view_all_leads === true || currentUser?.role === 'superadmin';

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [type, setType] = useState('todo');
  const [dueDate, setDueDate] = useState(null);
  const [assignedTo, setAssignedTo] = useState('');
  const [customFields, setCustomFields] = useState({});
  const [editingTask, setEditingTask] = useState(null);

  const { data: orgUsersResp } = useQuery(
    ['organization', 'users'],
    () => organizationsAPI.getUsers(),
    { enabled: canAssignAnyone, staleTime: 60_000 }
  );
  const orgUsers = orgUsersResp?.data?.users || [];

  const { data: taskDefs = [] } = useQuery(
    ['custom-fields', 'task'],
    () => api.get('/custom-fields', { params: { entity: 'task' } }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );

  const { data: tasks = [], isLoading } = useQuery(
    ['tasks', 'lead', leadId],
    () => api.get('/tasks', { params: { leadId } }).then((r) => r.data),
    { enabled: !!leadId }
  );

  const createMutation = useMutation(
    (body) => api.post('/tasks', { ...body, leadId }).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Task added');
        queryClient.invalidateQueries(['tasks']);
        setTitle(''); setDueDate(null); setType('todo'); setAssignedTo('');
        setCustomFields({}); setShowForm(false);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Create failed')
    }
  );

  const patchMutation = useMutation(
    ({ id, patch }) => api.patch(`/tasks/${id}`, patch).then((r) => r.data),
    { onSuccess: () => queryClient.invalidateQueries(['tasks']) }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/tasks/${id}`).then((r) => r.data),
    { onSuccess: () => queryClient.invalidateQueries(['tasks']) }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error('Title is required');
    const payload = {
      title: title.trim(),
      type,
      dueDate: dueDate ? dueDate.toISOString() : null,
    };
    if (canAssignAnyone && assignedTo) payload.assignedTo = assignedTo;
    if (Object.keys(customFields).length > 0) payload.customFields = customFields;
    createMutation.mutate(payload);
  };

  const active = tasks.filter((t) => !t.completed);
  const done = tasks.filter((t) => t.completed);

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <CheckSquare size={20} className="mr-2" />
          Tasks
          {active.length > 0 && <span className="ml-2 text-xs text-gray-500">({active.length} open)</span>}
        </h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="text-sm bg-primary-600 hover:bg-primary-700 text-white px-3 py-1 rounded-md"
        >
          {showForm ? 'Cancel' : '+ Add'}
        </button>
      </div>
      <div className="card-body space-y-3">
        {showForm && (
          <form onSubmit={handleSubmit} className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg">
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What needs to be done?"
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-md"
            />
            <div className="flex gap-2">
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-md"
              >
                {Object.entries(TYPE_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <DatePicker
                selected={dueDate}
                onChange={(d) => setDueDate(d)}
                showTimeSelect
                timeIntervals={15}
                dateFormat="MMM d, yyyy h:mm aa"
                placeholderText="Due date & time"
                minDate={new Date()}
                className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-md w-full"
                isClearable
              />
            </div>
            {canAssignAnyone && (
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
              >
                <option value="">Assign to me (default)</option>
                {orgUsers.map((u) => (
                  <option key={u._id} value={u._id}>{u.name || u.email}</option>
                ))}
              </select>
            )}
            {taskDefs.length > 0 && (
              <div className="pt-2 border-t border-gray-200 mt-1">
                <CustomFieldsInput entity="task" values={customFields} onChange={setCustomFields} />
              </div>
            )}
            <button
              type="submit"
              disabled={createMutation.isLoading}
              className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm rounded-md"
            >
              {createMutation.isLoading ? 'Adding...' : 'Add Task'}
            </button>
          </form>
        )}

        {isLoading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : tasks.length === 0 ? (
          <p className="text-gray-400 text-sm italic">No tasks for this lead.</p>
        ) : (
          <>
            {active.map((t) => {
              const meta = TYPE_META[t.type] || TYPE_META.todo;
              const { Icon } = meta;
              const accent = dueAccent(t.dueDate);
              return (
                <div key={t._id} className={`flex items-start gap-2 p-2 rounded-md border ${accent.row}`}>
                  <input
                    type="checkbox"
                    checked={t.completed}
                    onChange={() => patchMutation.mutate({ id: t._id, patch: { completed: !t.completed } })}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Icon size={12} className={meta.color} />
                      <span className="text-sm text-gray-900">{t.title}</span>
                    </div>
                    <div className={`text-[11px] mt-0.5 ${accent.text}`}>
                      {formatDue(t.dueDate)}
                    </div>
                    <div className="text-[10px] text-gray-400 mt-0.5 flex flex-wrap gap-x-2">
                      {t.createdBy && (
                        <span>by <span className="text-gray-600">{t.createdBy.name || t.createdBy.email}</span></span>
                      )}
                      {t.assignedTo && t.assignedTo._id !== t.createdBy?._id && (
                        <span>→ <span className="text-gray-600">{t.assignedTo.name || t.assignedTo.email}</span></span>
                      )}
                    </div>
                    {t.customFields && (
                      <CustomFieldsDisplay entity="task" values={t.customFields} wrapperClassName="mt-1.5 p-1.5 bg-white rounded border border-gray-100" />
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingTask(t)}
                      className="text-gray-400 hover:text-primary-600"
                      title="Edit"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      onClick={() => window.confirm('Delete this task?') && deleteMutation.mutate(t._id)}
                      className="text-gray-400 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
            {done.length > 0 && (
              <details className="mt-2">
                <summary className="text-xs text-gray-500 cursor-pointer">
                  {done.length} completed
                </summary>
                <div className="space-y-1 mt-2">
                  {done.map((t) => (
                    <div key={t._id} className="flex items-center gap-2 text-xs text-gray-400">
                      <input
                        type="checkbox"
                        checked
                        onChange={() => patchMutation.mutate({ id: t._id, patch: { completed: false } })}
                      />
                      <span className="line-through flex-1">{t.title}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </div>

      <TaskEditModal
        task={editingTask}
        isOpen={!!editingTask}
        onClose={() => setEditingTask(null)}
        onSave={(task, patch) => {
          patchMutation.mutate({ id: task._id, patch });
          setEditingTask(null);
        }}
        onDelete={(task) => {
          deleteMutation.mutate(task._id);
          setEditingTask(null);
        }}
        onToggle={(task) => patchMutation.mutate({ id: task._id, patch: { completed: !task.completed } })}
        lockedLeadId={leadId}
      />
    </div>
  );
};

// Inline edit form — replaces the row when the pencil is clicked. Saves
// title / type / dueDate / assignedTo (admin only) in one PATCH.
const InlineEditTask = ({ task, canAssignAnyone, orgUsers, onSave, onCancel }) => {
  const [title, setTitle] = useState(task.title || '');
  const [type, setType] = useState(task.type || 'todo');
  const [dueDate, setDueDate] = useState(task.dueDate ? new Date(task.dueDate) : null);
  const [assignedTo, setAssignedTo] = useState(task.assignedTo?._id || task.assignedTo || '');

  const save = () => {
    if (!title.trim()) return toast.error('Title is required');
    const patch = {
      title: title.trim(),
      type,
      dueDate: dueDate ? dueDate.toISOString() : null,
    };
    if (canAssignAnyone) patch.assignedTo = assignedTo || null;
    onSave(patch);
  };

  return (
    <div className="p-2 rounded-md border border-primary-200 bg-primary-50 space-y-2">
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md"
      />
      <div className="flex flex-wrap gap-2">
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
        >
          {Object.entries(TYPE_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <DatePicker
          selected={dueDate}
          onChange={(d) => setDueDate(d)}
          showTimeSelect
          timeIntervals={15}
          dateFormat="MMM d, yyyy h:mm aa"
          placeholderText="No due date"
          className="px-2 py-1.5 text-sm border border-gray-200 rounded-md"
          isClearable
        />
        {canAssignAnyone && (
          <select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
          >
            <option value="">Unassigned</option>
            {orgUsers.map((u) => (
              <option key={u._id} value={u._id}>{u.name || u.email}</option>
            ))}
          </select>
        )}
      </div>
      <div className="flex gap-2">
        <button onClick={save} className="inline-flex items-center gap-1 px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white text-xs rounded-md">
          <Check size={12} /> Save
        </button>
        <button onClick={onCancel} className="inline-flex items-center gap-1 px-3 py-1 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 text-xs rounded-md">
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
};

export default LeadTasksWidget;
